import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import useCollections from '@/hooks/useCollections';

import { MarkIncorrect } from './AmendRows';
import { ExpandToggle, PlainRow } from './DetailsRow';
import { DocumentLine } from './DocumentLine';
import { buildKnowledgeSources, KnowledgeSource } from './knowledgeSources';
import { ConversationTurn } from '../../../types/conversation';

type Props = { turn: ConversationTurn };

export const useKnowledgeSources = (turn: ConversationTurn) => {
  const knowledgeBase = turn.metadata.knowledgeBase;
  const collectionId = knowledgeBase?.id ?? turn.metadata.collection?.collection_id;
  const contents = knowledgeBase?.contents ?? turn.metadata.collection?.fragments ?? [];

  const { collection, getStatus } = useCollections(collectionId);

  return {
    collectionId,
    collection,
    isLoadingCollection: getStatus === 'pending',
    ...buildKnowledgeSources(contents, turn.metadata.collection?.external_urls ?? []),
  };
};

const KnowledgeSearchHead = ({ turn }: Props) => {
  const { t } = useTranslation();
  const { retrieved, selected } = useKnowledgeSources(turn);

  return (
    <PlainRow>
      {t('try_it.details.searched_knowledge_base')}
      {retrieved > 0 &&
        ` · ${t('try_it.details.sources_retrieved', { count: retrieved })}, ${t('try_it.details.sources_selected_short', { count: selected })}`}
    </PlainRow>
  );
};

export type SearchMode = 'semantic' | 'keyword';

/** Keyword scores count how often the term occurs, so they read as matches, not as a similarity. */
const useScoreLabel = (mode: SearchMode) => {
  const { t } = useTranslation();

  return (score?: number) => {
    if (mode !== 'keyword' || score === undefined) {
      return undefined;
    }
    return t('try_it.details.matches', { count: Math.round(score) });
  };
};

export const SourceList = ({
  collectionId,
  sources,
  mode = 'semantic',
}: {
  collectionId: string;
  sources: KnowledgeSource[];
  mode?: SearchMode;
}) => {
  const scoreLabel = useScoreLabel(mode);
  const { collection } = useCollections(collectionId);

  if (sources.length === 0 || !collection) {
    return null;
  }

  return (
    <div className="flex flex-col items-start gap-4 self-stretch pl-1">
      <ol className="flex flex-col items-start gap-3 self-stretch">
        {sources.map((source) => (
          <DocumentLine
            key={source.key}
            collectionId={collectionId}
            datasourceId={source.datasourceId}
            documentId={source.documentId}
            name={source.name}
            score={source.score}
            scoreLabel={scoreLabel(source.score)}
            isSelected={source.isSelected}
          />
        ))}
      </ol>
    </div>
  );
};

export const KnowledgeSearchRow = ({ turn }: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { collectionId, collection, isLoadingCollection, retrieved, sources } =
    useKnowledgeSources(turn);

  // Only call it deleted once the lookup has settled, or it flashes on every open.
  if (isLoadingCollection) {
    return <Skeleton className="h-5 w-64" />;
  }

  if (!collection) {
    return (
      <div className="flex flex-col items-start gap-4 pb-2">
        <PlainRow>{t('activityLogs.collection_deleted', { count: retrieved })}</PlainRow>
        <MarkIncorrect turn={turn} />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col items-start gap-4 pb-2">
      <div className="flex items-start justify-between gap-3 self-stretch">
        <span className="min-w-0 flex-1">
          <KnowledgeSearchHead turn={turn} />
        </span>
        {sources.length > 0 && (
          <ExpandToggle
            expanded={expanded}
            onToggle={() => setExpanded((value) => !value)}
            label={t('common.show_more')}
          />
        )}
      </div>
      {expanded && collectionId && <SourceList collectionId={collectionId} sources={sources} />}
      <MarkIncorrect turn={turn} />
    </div>
  );
};
